import React, { useEffect, useState } from "react";
import { moduleByKey } from "@/modules/registry";
import { useAuth } from "@/contexts/AuthContext";
import { api, peso } from "@/lib/api";
import { MetricCard, SubItemRow } from "@/pages/_ModuleLandingParts";

/**
 * Dashboard-style module welcome page — Ali Sayed / high.level layout.
 *   Row 1: eyebrow + Welcome greeting | right side: total headline stat
 *   Row 2: bar chart + growth pill
 *   Row 3: 3 mini metric spark cards
 *   Row 4: two columns of branch progress rows (with sub-item nav rows)
 */
export default function ModuleLanding({ moduleKey }) {
  const { user, hasPerm, orgId, branchId } = useAuth();
  const mod = moduleByKey(moduleKey);
  const [summary, setSummary] = useState(null);
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    if (!orgId) return;
    if (hasPerm("dashboard.view")) {
      const params = branchId ? { branch_id: branchId } : {};
      api.get("/dashboard/summary", { params }).then(({data}) => setSummary(data)).catch(()=>{});
    }
    api.get("/branches").then(({ data }) => setBranches(data)).catch(()=>{});
  }, [orgId, branchId, hasPerm]);

  if (!mod) return null;
  const firstName = (user?.name || user?.email || "Operator").split(" ")[0];
  const items = (mod.items || []).filter((it) => !it.perm || hasPerm(it.perm));

  const headline = HEADLINES[mod.key] || { label: "Modules available", value: items.length };
  const value = typeof headline.value === "function" ? headline.value(summary, items) : headline.value;

  return (
    <div className="relative p-8 lg:p-14 min-h-full" data-testid={`module-landing-${mod.key}`}>
      {/* Row 1 — greeting + headline stat */}
      <div className="flex flex-wrap items-start justify-between gap-8 animate-fade-up">
        <div>
          <div className="section-eyebrow ink-subtle">{mod.name}</div>
          <h1 className="mt-4 display-heading text-4xl md:text-5xl ink">
            Welcome {firstName}!
          </h1>
          <p className="mt-3 ink-muted text-sm max-w-md leading-relaxed">
            {mod.description}
          </p>
        </div>
        <div className="text-right">
          <div className="section-eyebrow">{headline.label}</div>
          <div className="mt-3 display-heading text-5xl ink mono">{value ?? "—"}</div>
          {headline.sub && (
            <div className="mt-1 tiny-label ink-subtle">{headline.sub(summary)}</div>
          )}
        </div>
      </div>

      {/* Row 2 — spark cards linking to sub-items */}
      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-8 animate-fade-up animate-fade-up-2">
        {items.length === 0 ? (
          <div className="col-span-full text-center py-12 ink-subtle text-sm">
            No workspaces available for your current permissions.
          </div>
        ) : (
          items.slice(0, 6).map((it, idx) => (
            <MetricCard key={it.path} item={it} seed={mod.key + idx} num={idx + 1}/>
          ))
        )}
      </div>

      {/* Row 3 — remaining sub-items list */}
      {items.length > 6 && (
        <div className="mt-16 animate-fade-up animate-fade-up-3">
          <div className="section-eyebrow mb-5">More workspaces</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-2">
            {items.slice(6).map((it) => (
              <SubItemRow key={it.path} item={it}/>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- Per-module headline hints ---------- */
const HEADLINES = {
  pos:          { label: "Total gross today", value: (s) => s ? peso(s.gross_sales) : "—", sub: (s) => s ? `${s.open_orders} open · ${s.occupied_tables}/${s.total_tables} tables` : "" },
  masterfiles:  { label: "Modules available", value: (s, items) => items.length },
  payroll:      { label: "Payroll status", value: "Ready" },
  reservations: { label: "Tonight's bookings", value: "0" },
  events:       { label: "Active events", value: "0" },
  security:     { label: "Current occupancy", value: (s) => s ? `${s.occupied_tables}/${s.total_tables}` : "—" },
  inventory:    { label: "Low-stock items", value: (s) => s?.low_stock_items ?? 0 },
  finance:      { label: "Cash sessions open", value: "0" },
  reports:      { label: "Data streams", value: (s, items) => items.length },
  team:         { label: "Sub-workspaces", value: (s, items) => items.length },
  audit:        { label: "Audit log active", value: "✓" },
  operations:   { label: "Modules available", value: (s, items) => items.length },
  guests:       { label: "Modules available", value: (s, items) => items.length },
  marketing:    { label: "Modules available", value: (s, items) => items.length },
  purchasing:   { label: "Modules available", value: (s, items) => items.length },
  facilities:   { label: "Modules available", value: (s, items) => items.length },
};

/* ---------- Sub components ---------- */
